import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-white hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        ghostDestructive: 'text-red-500 hover:text-red-700 hover:bg-transparent',
        sectorCard:
          'flex flex-col items-center justify-center gap-4 whitespace-normal border-2 shadow-elevation-1 hover:bg-transparent',
        link: 'text-primary underline-offset-4 hover:underline',
        tableLink: 'text-brand-primary underline-offset-4 hover:underline',
        brand: 'bg-brand-primary text-white hover:opacity-90 transition-colors',
        auth: 'bg-brand-primary text-white hover:bg-brand-primary/90 disabled:pointer-events-auto disabled:cursor-not-allowed',
        brandOutline:
          'border border-brand-primary text-brand-primary hover:bg-brand-tint transition-colors',
        success: 'bg-success text-white hover:opacity-90 transition-colors',
        error: 'bg-error-dark text-white hover:opacity-90 transition-colors',
        warning: 'bg-yellow-400 text-yellow-900 hover:bg-yellow-500 transition-colors',
        quizChoice:
          'h-auto py-3 w-full justify-start border font-normal whitespace-normal text-left bg-white text-gray-body border-gray-placeholder/50 hover:border-brand-primary hover:bg-brand-tint/50',
      },
      size: {
        default: 'h-11 px-4 py-2.5 rounded',
        sm: 'h-9 rounded px-3',
        lg: 'h-11 rounded px-8',
        icon: 'h-10 w-10 rounded',
        auth: 'h-11 rounded px-4 text-lg font-medium',
        formAction: 'h-11 rounded px-6 py-2.5 text-base font-medium leading-6',
        sectorCard: 'w-65 h-42 rounded p-4',
      },
    },
    compoundVariants: [{ variant: 'tableLink', size: 'default', class: 'p-0 h-auto text-sm' }],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export type ButtonVariant = NonNullable<ButtonVariantProps['variant']>;
export type ButtonSize = NonNullable<ButtonVariantProps['size']>;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, Omit<ButtonVariantProps, 'variant' | 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = 'button', ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
